import { useCallback, useEffect, useState } from 'react';
import { Card, Icon, Tone } from '~/components/ui';
import { useLocale, useT } from '~/i18n';
import {
  suggestReply,
  type ReplyOptionId,
  type ReplySuggestion,
  type ReplyTypology,
} from '../api/threadsClient';
import { isNothingToSuggest, suggestionErrorMessage } from '../lib/replySuggestion';

interface Props {
  applicationId: string;
  /** Pone el borrador en la caja de respuesta. NO envía. */
  onUse: (text: string) => void;
}

/**
 * "Respuesta sugerida": qué te dijo la empresa, qué conviene contestar, las
 * opciones de respuesta para ese tipo de mensaje y de 1 a 3 borradores listos
 * para usar.
 *
 * Se pide sola al abrir el hilo: el servidor la guarda por mensaje, así que
 * volver al mismo hilo no la regenera. Tocar una opción («Confirmar
 * disponibilidad», «Pedir que me tengan en cuenta»…) pide borradores escritos
 * para esa opción (también guardados, por mensaje + opción). "Ver otras
 * opciones" pide otra tanda de lo que se está viendo.
 *
 * Nunca estorba: si el servidor dice que no hay nada que sugerir (409) la
 * tarjeta desaparece, y si falla, el compositor de abajo sigue funcionando igual
 * que siempre. Nada se envía solo: "Usar esta respuesta" sólo llena la caja.
 */
export default function SuggestedReplyCard({ applicationId, onUse }: Props) {
  const t = useT('workspace').suggestion;
  const locale = useLocale();
  const [suggestion, setSuggestion] = useState<ReplySuggestion | null>(null);
  const [options, setOptions] = useState<ReplyOptionId[]>([]);
  const [optionsTypology, setOptionsTypology] = useState<ReplyTypology | null>(null);
  /** La opción cuyos borradores se están mostrando; null = los de la sugerencia general. */
  const [activeOption, setActiveOption] = useState<ReplyOptionId | null>(null);
  /** La opción que se está pidiendo ahora (los botones siguen a la vista mientras tanto). */
  const [pendingOption, setPendingOption] = useState<ReplyOptionId | null>(null);
  const [loading, setLoading] = useState(true);
  // El error crudo, no la frase: se traduce al pintar, así cambia con el idioma.
  const [error, setError] = useState<unknown>(null);
  const [hidden, setHidden] = useState(false);
  const [usedIndex, setUsedIndex] = useState<number | null>(null);

  // Todo el setState vive en los callbacks de la promesa, no en el cuerpo del
  // efecto: así la carga inicial, las opciones y "Ver otras opciones" comparten
  // un solo camino.
  const fetchInto = useCallback(
    (refresh: boolean, option: ReplyOptionId | null) =>
      suggestReply(applicationId, option ? { refresh, option } : { refresh })
        .then((data) => {
          setSuggestion(data.suggestion);
          // Un servidor viejo no manda opciones: la tarjeta queda como antes.
          if (Array.isArray(data.options)) setOptions(data.options);
          if (data.options_typology) setOptionsTypology(data.options_typology);
          setActiveOption(option);
          setUsedIndex(null);
        })
        .catch((err: unknown) => {
          if (isNothingToSuggest(err)) setHidden(true);
          else setError(err ?? new Error('unknown'));
        })
        .finally(() => {
          setLoading(false);
          setPendingOption(null);
        }),
    [applicationId],
  );

  // Un mensaje nuevo de la empresa remonta la tarjeta (la página la monta con
  // `key={messageId}`), así que este efecto sólo corre al abrir.
  useEffect(() => {
    void fetchInto(false, null);
  }, [fetchInto]);

  const reload = (refresh: boolean) => {
    setLoading(true);
    setError(null);
    void fetchInto(refresh, activeOption);
  };

  const choose = (option: ReplyOptionId) => {
    if (pendingOption) return;
    setError(null);
    setPendingOption(option);
    void fetchInto(false, option);
  };

  if (hidden) return null;

  const use = (index: number, body: string) => {
    onUse(body);
    setUsedIndex(index);
  };

  const typology = optionsTypology ?? suggestion?.typology;
  const busy = loading || pendingOption != null;

  return (
    <Card
      scope={t.scope}
      title={
        suggestion && typology ? (
          <span className="flex items-center gap-2">
            <span className="text-brand">
              <Icon name="sparkle" size={18} />
            </span>
            {t.typology[typology] ?? t.typology.other}
          </span>
        ) : undefined
      }
      trail={
        suggestion && !busy ? (
          <button className="btn btn-ghost btn-xs gap-1" onClick={() => reload(true)}>
            <Icon name="refresh" size={14} />
            {t.regenerate}
          </button>
        ) : undefined
      }
      foot={suggestion ? <span>{t.note}</span> : undefined}
    >
      {loading && (
        <div className="flex items-center gap-2 text-sm hush" role="status" aria-live="polite">
          <span className="loading loading-spinner loading-xs" />
          {t.loading}
        </div>
      )}

      {!loading && error != null && (
        <div className="alert alert-warning text-sm mb-4" role="alert">
          <span>{suggestionErrorMessage(error, locale)}</span>
          <button className="btn btn-ghost btn-sm" onClick={() => reload(false)}>
            {t.retry}
          </button>
        </div>
      )}

      {!loading && suggestion && (
        <div className="space-y-4">
          <p className="text-sm">{suggestion.explanation}</p>

          {options.length > 0 && (
            <div className="space-y-2">
              <div className="space-y-1">
                <span className="brand-label block">{t.optionsTitle}</span>
                <p className="text-xs hush">{t.optionsNote}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {options.map((option) => {
                  const active = activeOption === option;
                  return (
                    <button
                      key={option}
                      type="button"
                      className={`btn btn-sm ${active ? 'btn-primary' : 'btn-outline'}`}
                      aria-pressed={active}
                      disabled={pendingOption != null}
                      onClick={() => choose(option)}
                    >
                      {pendingOption === option && <span className="loading loading-spinner loading-xs" />}
                      {t.replyOptions[option] ?? option}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {pendingOption ? (
            <div className="flex items-center gap-2 text-sm hush" role="status" aria-live="polite">
              <span className="loading loading-spinner loading-xs" />
              {t.optionLoading}
            </div>
          ) : (
            <>
              <div className="space-y-1">
                <span className="brand-label block">{t.draftsTitle}</span>
                <p className="text-xs hush">{t.languageNote}</p>
              </div>

              <ul className="space-y-3">
                {suggestion.drafts.map((draft, index) => (
                  <li
                    key={`${suggestion.generated_at}-${index}`}
                    className="rounded-box p-3 space-y-2"
                    style={{ border: '1px solid var(--hairline)' }}
                  >
                    <span className="text-xs font-medium block">{draft.label || t.option(index + 1)}</span>
                    <p className="text-sm whitespace-pre-line" lang={suggestion.employer_language}>
                      {draft.body}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        className="btn btn-primary btn-sm gap-2"
                        onClick={() => use(index, draft.body)}
                        aria-label={`${t.use}: ${draft.label || t.option(index + 1)}`}
                      >
                        <Icon name="arrow-right" />
                        {t.use}
                      </button>
                      {usedIndex === index && <Tone tone="good">{t.used}</Tone>}
                    </div>
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}
    </Card>
  );
}
